import { unwrap, type Schemas } from '@mrigl/api-client';
import type { MessageKey } from '@mrigl/i18n';
import { Alert, Button, SelectField } from '@mrigl/ui';
import { useId, useState, type ChangeEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import {
  GridBody,
  GridToolbar,
  Pagination,
  SearchFilter,
  SortHeader,
  useGridState,
  usePagedData,
  useRowLink,
  type ListQuery,
} from '../../grid.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, useStore, type StoreDetails } from './shared.js';

type ProductSummary = Schemas['ProductSummary'];
type ImportReport = Schemas['ProductImportReport'];
type Status = ProductSummary['status'];

const STATUSES: readonly Status[] = ['draft', 'active', 'archived'];

type ProductsQuery = ListQuery<'/admin-api/v1/stores/{storeId}/products'>;

/** Store products: search, status filter, sortable columns, pages, CSV export/import. */
export function ProductsPage() {
  const { storeId = '' } = useParams();
  const { t, locale, formatDateTime, formatPrice } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const grid = useGridState({
    sort: '-updatedAt',
    sortable: ['title', 'price', 'updatedAt', 'createdAt'],
    filters: { q: '', status: '' },
  });
  const rowLink = useRowLink();
  const { filters } = grid;
  const key = JSON.stringify([storeId, locale, grid.page, grid.pageSize, grid.sort, filters]);
  const { data, loading, error, reload } = usePagedData<ProductSummary>(
    key,
    () =>
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/products', {
          params: {
            path: { storeId },
            query: {
              page: grid.page,
              pageSize: grid.pageSize,
              sort: grid.sort as NonNullable<ProductsQuery['sort']>,
              locale,
              ...(filters.q === '' ? {} : { q: filters.q }),
              ...(filters.status === '' ? {} : { status: filters.status as Status }),
            },
          },
        }),
      ),
    grid.setPage,
  );

  if (storeError !== null) return <Alert tone="error">{storeError}</Alert>;
  if (store === null) return <Loading />;
  const canManage = store.permissions.includes('catalog.manage');
  const header = (field: string, label: string, firstDescending = false, numeric = false) => (
    <SortHeader
      field={field}
      label={label}
      sort={grid.sort}
      onSort={grid.setSort}
      firstDescending={firstDescending}
      numeric={numeric}
    />
  );

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <div className="admin-title-row">
        <h1 className="mrigl-heading">{t('admin.catalog.products')}</h1>
        {canManage ? (
          <Link
            className="mrigl-button mrigl-button--primary admin-cta"
            to={`/stores/${storeId}/products/new`}
          >
            {t('admin.product.new')}
          </Link>
        ) : null}
      </div>
      <GridToolbar
        activeFilters={grid.activeFilters}
        onReset={grid.reset}
        search={
          <SearchFilter
            label={t('admin.product.search')}
            value={filters.q}
            onChange={(value) => {
              grid.setFilter('q', value);
            }}
          />
        }
      >
        <SelectField
          label={t('admin.product.statusFilter')}
          value={filters.status}
          onChange={(event) => {
            grid.setFilter('status', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.product.allStatuses') },
            ...STATUSES.map((value) => ({ value, label: t(`admin.product.status.${value}`) })),
          ]}
        />
      </GridToolbar>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {data === null ? (
        loading ? (
          <Loading />
        ) : null
      ) : (
        <>
          <GridBody
            loading={loading}
            empty={data.items.length === 0}
            filtered={grid.activeFilters > 0}
            emptyText={t('admin.product.empty')}
          >
            <table className="mrigl-table">
              <thead>
                <tr>
                  {header('title', t('admin.product.column.product'))}
                  <th scope="col">{t('admin.product.column.status')}</th>
                  <th scope="col" className="admin-num">
                    {t('admin.product.column.variants')}
                  </th>
                  {header('price', t('admin.product.column.price'), false, true)}
                  {header('updatedAt', t('admin.product.column.updated'), true)}
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr
                    key={item.productId}
                    {...rowLink(`/stores/${storeId}/products/${item.productId}`)}
                  >
                    <td>
                      <span className="admin-product-cell">
                        {item.thumbnailUrl === null ? (
                          <span className="admin-thumb admin-thumb--empty" aria-hidden="true" />
                        ) : (
                          <img
                            className="admin-thumb"
                            src={item.thumbnailUrl}
                            alt=""
                            width={48}
                            height={48}
                          />
                        )}
                        <Link
                          className="admin-cell-link"
                          to={`/stores/${storeId}/products/${item.productId}`}
                        >
                          {display(item.title, locale, store, item.handle)}
                        </Link>
                      </span>
                    </td>
                    <td>
                      <span className={`admin-status admin-status--product-${item.status}`}>
                        {t(`admin.product.status.${item.status}`)}
                      </span>
                    </td>
                    <td className="admin-num">{item.variantCount}</td>
                    <td className="mrigl-ltr admin-num admin-nowrap">
                      {item.priceMin === null || item.priceMax === null
                        ? '—'
                        : item.priceMin.minor === item.priceMax.minor
                          ? formatPrice(item.priceMin.minor)
                          : t('admin.product.priceRange', {
                              min: formatPrice(item.priceMin.minor),
                              max: formatPrice(item.priceMax.minor),
                            })}
                    </td>
                    <td className="admin-nowrap">{formatDateTime(item.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GridBody>
          <Pagination
            page={grid.page}
            pageSize={grid.pageSize}
            total={data.total}
            onPage={grid.setPage}
            onPageSize={grid.setPageSize}
          />
        </>
      )}
      <CsvSection store={store} canImport={canManage} onImported={reload} />
    </div>
  );
}

function importErrorText(t: I18n['t'], error: ImportReport['errors'][number]): string {
  const message = t(`admin.catalog.importError.${error.code}` as MessageKey);
  return error.column === null
    ? t('admin.catalog.importErrorLine', { line: error.line, message })
    : t('admin.catalog.importErrorCell', { line: error.line, column: error.column, message });
}

function CsvSection({
  store,
  canImport,
  onImported,
}: {
  readonly store: StoreDetails;
  readonly canImport: boolean;
  readonly onImported: () => void;
}) {
  const { t } = useI18n();
  const inputId = useId();
  const [csv, setCsv] = useState<string | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const send = (body: string, dryRun: boolean) =>
    unwrap(
      api.POST('/admin-api/v1/stores/{storeId}/products/import', {
        params: { path: { storeId: store.storeId }, query: { dryRun: dryRun ? 'true' : 'false' } },
        body,
        bodySerializer: (value: string) => value,
        headers: { 'content-type': 'text/csv' },
      }),
    );

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (file === undefined) return;
    setReport(null);
    setError(null);
    setStatus(t('admin.catalog.importChecking'));
    setBusy(true);
    try {
      const text = await file.text();
      setCsv(text);
      setReport(await send(text, true));
      setStatus(null);
    } catch (failure) {
      setStatus(null);
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (csv === null) return;
    setBusy(true);
    setError(null);
    try {
      const result = await send(csv, false);
      if (result.errors.length > 0) {
        setReport(result);
      } else {
        setReport(null);
        setCsv(null);
        setStatus(
          t('admin.catalog.importDone', { created: result.created, updated: result.updated }),
        );
        onImported();
      }
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mrigl-card mrigl-stack" aria-labelledby={`${inputId}-title`}>
      <h2 id={`${inputId}-title`} className="mrigl-subheading">
        {t('admin.catalog.csv')}
      </h2>
      <a href={`/admin-api/v1/stores/${store.storeId}/products/export`} download>
        {t('admin.catalog.export')}
      </a>
      {canImport ? (
        <>
          <label className="admin-upload" htmlFor={inputId}>
            <span className="mrigl-button">{t('admin.catalog.import')}</span>
            <input
              id={inputId}
              className="admin-upload__input"
              type="file"
              accept=".csv,text/csv"
              disabled={busy}
              aria-describedby={`${inputId}-hint`}
              onChange={(event) => void onFile(event)}
            />
          </label>
          <p id={`${inputId}-hint`} className="mrigl-muted">
            {t('admin.catalog.importHint')}
          </p>
          <div role="status">{status === null ? null : <p>{status}</p>}</div>
          {error === null ? null : <Alert tone="error">{error}</Alert>}
          {report === null ? null : report.errors.length > 0 ? (
            <Alert tone="error">
              <p>{t('admin.catalog.importErrors')}</p>
              <ul className="mrigl-list">
                {report.errors.map((item) => (
                  <li key={`${String(item.line)}-${item.column ?? ''}-${item.code}`}>
                    {importErrorText(t, item)}
                  </li>
                ))}
              </ul>
            </Alert>
          ) : (
            <div className="mrigl-stack">
              <p>
                {t('admin.catalog.importSummary', {
                  products: report.products,
                  created: report.created,
                  updated: report.updated,
                })}
              </p>
              <Button busy={busy} onClick={() => void apply()}>
                {t('admin.catalog.importApply')}
              </Button>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}
